import type { SubGamesSDK } from './subgames-sdk';
import type { SubGamesConfig } from '../types';

interface GameInfo {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  gameUrl: string;
  creatorId: string | null;
  creator?: {
    id: string;
    displayName: string | null;
    slug: string | null;
  } | null;
}

interface CreatorProfile {
  id: string;
  profileImageUrl: string | null;
  freeSubscriberCount: number;
  supporterCount: number;
  founderCount: number;
  user?: { slug: string | null; displayName: string | null; avatarUrl: string | null };
}

function defaultAvatarUrl(name: string): string {
  return `https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(name)}`;
}

const OVERLAY_CSS = `
.sg-overlay { position: fixed; top: 16px; right: 16px; z-index: 99999; display: flex; flex-direction: column; align-items: flex-end; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
.sg-avatar-btn { width: 40px; min-width: 40px; height: 40px; border-radius: 50%; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.4); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); cursor: pointer; overflow: hidden; padding: 0; box-shadow: 0 4px 24px rgba(0,0,0,0.3); transition: background 0.2s; flex-shrink: 0; }
.sg-avatar-btn:hover { background: rgba(0,0,0,0.6); }
.sg-avatar-btn img { width: 100%; height: 100%; object-fit: cover; }
.sg-dropdown { margin-top: 8px; width: 224px; border-radius: 16px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.6); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); box-shadow: 0 8px 32px rgba(0,0,0,0.4); overflow: hidden; display: none; }
.sg-dropdown.sg-open { display: block; }
.sg-creator-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; cursor: pointer; transition: background 0.15s; text-decoration: none; }
.sg-creator-row:hover { background: rgba(255,255,255,0.05); }
.sg-creator-avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.sg-creator-info { min-width: 0; flex: 1; }
.sg-creator-name { font-size: 14px; font-weight: 500; color: white; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin: 0; }
.sg-creator-subs { font-size: 12px; color: rgba(255,255,255,0.4); margin: 0; }
.sg-divider { border: none; border-top: 1px solid rgba(255,255,255,0.1); margin: 0; }
.sg-game-info { padding: 12px 16px; }
.sg-game-name { font-size: 14px; font-weight: 600; color: white; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin: 0; }
.sg-game-desc { font-size: 12px; color: rgba(255,255,255,0.4); margin: 2px 0 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sg-actions { display: flex; align-items: center; justify-content: space-around; padding: 8px; }
.sg-action-btn { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 8px; border-radius: 8px; background: none; border: none; color: rgba(255,255,255,0.6); cursor: pointer; transition: background 0.15s, color 0.15s; }
.sg-action-btn:hover { background: rgba(255,255,255,0.1); }
.sg-action-btn.sg-active-like { color: #f87171; }
.sg-action-btn.sg-active-regame { color: #4ade80; }
.sg-action-btn svg { width: 20px; height: 20px; }
.sg-bottom { border-top: 1px solid rgba(255,255,255,0.1); padding: 8px; }
.sg-bottom-btn { display: flex; align-items: center; gap: 12px; width: 100%; padding: 8px 12px; border-radius: 8px; background: none; border: none; font-size: 14px; color: rgba(255,255,255,0.8); cursor: pointer; transition: background 0.15s; text-decoration: none; }
.sg-bottom-btn:hover { background: rgba(255,255,255,0.1); }
.sg-bottom-btn svg { width: 16px; height: 16px; }
.sg-comments-backdrop { position: fixed; inset: 0; z-index: 100000; }
.sg-comments-panel { position: fixed; z-index: 100001; bottom: 16px; right: 16px; width: 380px; max-height: 70vh; border-radius: 16px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.6); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); box-shadow: 0 8px 32px rgba(0,0,0,0.4); display: flex; flex-direction: column; overflow: hidden; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
@media (max-width: 639px) { .sg-comments-panel { bottom: 0; right: 0; width: 100%; height: 80vh; max-height: 80vh; border-radius: 16px 16px 0 0; } }
.sg-comments-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; }
.sg-comments-header h2 { font-size: 16px; font-weight: 600; color: white; margin: 0; }
.sg-comments-close { background: none; border: none; color: rgba(255,255,255,0.4); cursor: pointer; padding: 0; transition: color 0.15s; }
.sg-comments-close:hover { color: white; }
.sg-comments-close svg { width: 20px; height: 20px; }
.sg-comments-list { flex: 1; overflow-y: auto; padding: 20px; display: flex; flex-direction: column; gap: 16px; }
.sg-comments-empty { font-size: 14px; color: rgba(255,255,255,0.4); text-align: center; padding: 16px 0; }
.sg-comment-item { display: flex; gap: 12px; }
.sg-comment-avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; flex-shrink: 0; }
.sg-comment-body { min-width: 0; }
.sg-comment-name { font-size: 14px; font-weight: 500; color: white; margin: 0; }
.sg-comment-text { font-size: 14px; color: rgba(255,255,255,0.6); margin: 2px 0 0; word-break: break-word; }
.sg-comments-input { border-top: 1px solid rgba(255,255,255,0.1); padding: 16px; display: flex; gap: 12px; }
.sg-comments-textarea { flex: 1; border-radius: 8px; border: 1px solid rgba(255,255,255,0.1); background: rgba(255,255,255,0.05); padding: 8px 12px; font-size: 14px; color: white; resize: none; font-family: inherit; outline: none; }
.sg-comments-textarea::placeholder { color: rgba(255,255,255,0.3); }
.sg-comments-textarea:focus { border-color: rgba(255,255,255,0.3); }
.sg-comments-submit { align-self: flex-end; border-radius: 8px; background: #a3e635; border: none; padding: 8px 16px; font-size: 14px; font-weight: 500; color: black; cursor: pointer; transition: opacity 0.15s; }
.sg-comments-submit:disabled { opacity: 0.5; cursor: default; }
.sg-comments-submit:hover:not(:disabled) { opacity: 0.9; }
.sg-top-row { display: flex; align-items: center; gap: 8px; }
.sg-subscribe-pill { height: 36px; padding: 0 16px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.1); background: rgba(0,0,0,0.4); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); box-shadow: 0 4px 24px rgba(0,0,0,0.3); font-size: 13px; font-weight: 600; color: white; cursor: pointer; transition: background 0.2s; white-space: nowrap; font-family: inherit; }
.sg-subscribe-pill:hover { background: rgba(0,0,0,0.6); }
`;

// SVG icons (matching lucide-react icons from the web app)
const ICON_HEART = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>`;
const ICON_HEART_FILLED = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>`;
const ICON_COMMENT = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"/></svg>`;
const ICON_REGAME = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></svg>`;
const ICON_SHARE = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" x2="12" y1="2" y2="15"/></svg>`;
const ICON_HOME = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>`;
const ICON_BOOKMARK = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>`;
const ICON_BOOKMARK_FILLED = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/></svg>`;
const ICON_CLOSE = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;

function openSubscribeIframe(
  config: { appUrl: string },
  creatorSlug: string,
  gameId: string,
): void {
  // Prevent multiple overlays
  if (document.querySelector('.sg-subscribe-backdrop')) return;

  const backdrop = document.createElement('div');
  backdrop.className = 'sg-subscribe-backdrop';
  Object.assign(backdrop.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '100002',
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const iframe = document.createElement('iframe');
  iframe.src = `${config.appUrl}/@${creatorSlug}/sub?gameId=${gameId}`;
  Object.assign(iframe.style, {
    width: '420px',
    maxWidth: '95vw',
    height: '600px',
    maxHeight: '90vh',
    borderRadius: '16px',
    border: 'none',
    background: '#09090b',
  });

  backdrop.appendChild(iframe);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) backdrop.remove();
  });

  // Listen for subscription success or modal close from the iframe
  const handler = (event: MessageEvent) => {
    if (event.data?.type === 'subgames:subscribed' || event.data?.type === 'sg:subscription-changed' || event.data?.type === 'subgames:subscribe-closed') {
      backdrop.remove();
      window.removeEventListener('message', handler);
    }
  };
  window.addEventListener('message', handler);

  document.body.appendChild(backdrop);
}

export function createOverlay(
  sdk: SubGamesSDK,
  config: Required<Pick<SubGamesConfig, 'gameKey' | 'apiUrl' | 'appUrl'>> & SubGamesConfig,
): void {
  if (document.querySelector('.sg-overlay')) return;

  let game: GameInfo | null = null;
  let creator: CreatorProfile | null = null;
  let isOpen = false;
  let isLiked = false;
  let isRegamed = false;
  let isSaved = false;
  let playerTier: string = 'none';

  // Inject styles
  const style = document.createElement('style');
  style.textContent = OVERLAY_CSS;
  document.head.appendChild(style);

  // Create container
  const container = document.createElement('div');
  container.className = 'sg-overlay';
  document.body.appendChild(container);

  // Top row (subscribe pill + avatar) — hidden until game data loads
  const topRow = document.createElement('div');
  topRow.className = 'sg-top-row';
  topRow.style.display = 'none';
  container.appendChild(topRow);

  // Avatar button
  const avatarBtn = document.createElement('button');
  avatarBtn.className = 'sg-avatar-btn';
  avatarBtn.innerHTML = '<img src="" alt="" />';
  topRow.appendChild(avatarBtn);

  // Dropdown
  const dropdown = document.createElement('div');
  dropdown.className = 'sg-dropdown';
  container.appendChild(dropdown);

  function toggleDropdown(e: Event) {
    e.stopPropagation();
    isOpen = !isOpen;
    dropdown.classList.toggle('sg-open', isOpen);
  }

  avatarBtn.addEventListener('click', toggleDropdown);

  // Close on outside click
  document.addEventListener('click', () => {
    if (isOpen) {
      isOpen = false;
      dropdown.classList.remove('sg-open');
    }
  });

  // Close when page loses focus (iframe gets focus)
  window.addEventListener('blur', () => {
    if (isOpen) {
      isOpen = false;
      dropdown.classList.remove('sg-open');
    }
  });

  // Prevent dropdown clicks from closing
  dropdown.addEventListener('click', (e) => e.stopPropagation());

  // Fetch game info and render
  fetch(`${config.apiUrl}/games/by-key/${config.gameKey}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((data: GameInfo | null) => {
      if (!data) return;
      game = data;
      topRow.style.display = '';
      const creatorName = game.creator?.displayName || game.creator?.slug || 'Creator';
      const avatarUrl = defaultAvatarUrl(creatorName);

      // Update avatar button
      (avatarBtn.querySelector('img') as HTMLImageElement).src = avatarUrl;
      (avatarBtn.querySelector('img') as HTMLImageElement).alt = creatorName;

      const promises: Promise<void>[] = [];

      // Fetch creator profile
      if (game.creatorId) {
        promises.push(
          fetch(`${config.apiUrl}/creators/by-user/${game.creatorId}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((profile: CreatorProfile | null) => {
              if (profile) {
                creator = profile;
                const creatorImg = profile.profileImageUrl || profile.user?.avatarUrl;
                if (creatorImg) {
                  (avatarBtn.querySelector('img') as HTMLImageElement).src = creatorImg;
                }
              }
            })
            .catch(() => {})
        );
      }

      // Fetch player tier
      promises.push(
        sdk.getPlayerTier()
          .then((tier) => { playerTier = tier; })
          .catch(() => {})
      );

      // Fetch social status (like/regame) and save status if authenticated
      const token = sdk.getToken();
      if (token && game.id) {
        promises.push(
          fetch(`${config.apiUrl}/social/status?gameIds=${game.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then((r) => (r.ok ? r.json() : {}))
            .then((statusData: any) => {
              if (statusData[game!.id]) {
                isLiked = statusData[game!.id].liked;
                isRegamed = statusData[game!.id].regamed;
              }
            })
            .catch(() => {})
        );
        promises.push(
          fetch(`${config.apiUrl}/library/save/${game.id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((saveData: any) => {
              if (saveData) isSaved = saveData.saved;
            })
            .catch(() => {})
        );
      }

      Promise.all(promises).then(() => render(creatorName));

      // Re-render when auth state changes (e.g. silent auth completes after overlay init)
      sdk.on('tierChange', (newTier) => {
        playerTier = newTier;
        render(creatorName);
      });
      sdk.on('login', () => {
        // Refresh tier and social status after login
        sdk.getPlayerTier()
          .then((tier) => { playerTier = tier; render(creatorName); })
          .catch(() => {});
      });
    })
    .catch(() => {});

  function openCommentsPanel() {
    if (!game) return;
    // Close the dropdown
    isOpen = false;
    dropdown.classList.remove('sg-open');

    // Backdrop for click-outside
    const backdrop = document.createElement('div');
    backdrop.className = 'sg-comments-backdrop';
    backdrop.addEventListener('click', () => closePanel());

    // Panel
    const panel = document.createElement('div');
    panel.className = 'sg-comments-panel';
    panel.addEventListener('click', (e) => e.stopPropagation());

    // Header
    const header = document.createElement('div');
    header.className = 'sg-comments-header';
    const title = document.createElement('h2');
    title.textContent = 'Comments';
    const closeBtn = document.createElement('button');
    closeBtn.className = 'sg-comments-close';
    closeBtn.innerHTML = ICON_CLOSE;
    closeBtn.addEventListener('click', () => closePanel());
    header.appendChild(title);
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Divider
    const div = document.createElement('hr');
    div.className = 'sg-divider';
    panel.appendChild(div);

    // Comments list
    const list = document.createElement('div');
    list.className = 'sg-comments-list';
    const loading = document.createElement('p');
    loading.className = 'sg-comments-empty';
    loading.textContent = 'Loading...';
    list.appendChild(loading);
    panel.appendChild(list);

    // Fetch and render comments
    fetch(`${config.apiUrl}/social/comments/${game!.id}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((comments: any[]) => {
        list.innerHTML = '';
        if (!comments.length) {
          const empty = document.createElement('p');
          empty.className = 'sg-comments-empty';
          empty.textContent = 'No comments yet';
          list.appendChild(empty);
          return;
        }
        comments.forEach((c: any) => {
          list.appendChild(buildCommentEl(c));
        });
      })
      .catch(() => {
        list.innerHTML = '';
        const err = document.createElement('p');
        err.className = 'sg-comments-empty';
        err.textContent = 'Failed to load comments';
        list.appendChild(err);
      });

    // Input area (only if authenticated)
    const token = sdk.getToken();
    if (token) {
      const inputDiv = document.createElement('div');
      inputDiv.className = 'sg-comments-input';
      const textarea = document.createElement('textarea');
      textarea.className = 'sg-comments-textarea';
      textarea.placeholder = 'Add a comment...';
      textarea.rows = 2;
      const submitBtn = document.createElement('button');
      submitBtn.className = 'sg-comments-submit';
      submitBtn.textContent = 'Post';
      submitBtn.disabled = true;
      textarea.addEventListener('input', () => {
        submitBtn.disabled = !textarea.value.trim();
      });
      submitBtn.addEventListener('click', async () => {
        const text = textarea.value.trim();
        if (!text) return;
        submitBtn.disabled = true;
        submitBtn.textContent = '...';
        try {
          const res = await fetch(`${config.apiUrl}/social/comment/${game!.id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ text }),
          });
          if (res.ok) {
            const newComment = await res.json();
            textarea.value = '';
            // Remove "No comments yet" if present
            const empty = list.querySelector('.sg-comments-empty');
            if (empty) empty.remove();
            // Prepend new comment
            list.insertBefore(buildCommentEl(newComment), list.firstChild);
          }
        } catch {}
        submitBtn.textContent = 'Post';
        submitBtn.disabled = !textarea.value.trim();
      });
      inputDiv.appendChild(textarea);
      inputDiv.appendChild(submitBtn);
      panel.appendChild(inputDiv);
    }

    document.body.appendChild(backdrop);
    document.body.appendChild(panel);

    function closePanel() {
      backdrop.remove();
      panel.remove();
    }
  }

  function buildCommentEl(c: any): HTMLElement {
    const item = document.createElement('div');
    item.className = 'sg-comment-item';
    const avatar = document.createElement('img');
    avatar.className = 'sg-comment-avatar';
    avatar.src = c.user?.avatarUrl || defaultAvatarUrl(c.user?.displayName || 'User');
    avatar.alt = c.user?.displayName || 'User';
    const body = document.createElement('div');
    body.className = 'sg-comment-body';
    const name = document.createElement('p');
    name.className = 'sg-comment-name';
    name.textContent = c.user?.displayName || 'User';
    const text = document.createElement('p');
    text.className = 'sg-comment-text';
    text.textContent = c.text;
    body.appendChild(name);
    body.appendChild(text);
    item.appendChild(avatar);
    item.appendChild(body);
    return item;
  }

  function render(creatorName: string) {
    if (!game) return;
    const subCount = creator
      ? creator.freeSubscriberCount + creator.supporterCount + creator.founderCount
      : 0;
    const creatorSlug = creator?.user?.slug || game.creator?.slug || '';
    const creatorAvatar = creator?.profileImageUrl || creator?.user?.avatarUrl || defaultAvatarUrl(creatorName);

    // Subscribe/upgrade pill in top row
    const existingPill = topRow.querySelector('.sg-subscribe-pill');
    if (existingPill) existingPill.remove();

    {
      const pill = document.createElement('button');
      pill.className = 'sg-subscribe-pill';
      pill.textContent = playerTier === 'none' ? 'Subscribe' : 'Upgrade';
      pill.addEventListener('click', () => {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'subgames:open-subscribe' }, '*');
        } else {
          openSubscribeIframe(config, creatorSlug, game!.id);
        }
      });
      topRow.insertBefore(pill, avatarBtn);
    }

    dropdown.innerHTML = '';

    // Creator row
    if (creator) {
      const row = document.createElement('a');
      row.className = 'sg-creator-row';
      row.href = `${config.appUrl}/@${creatorSlug}`;
      row.target = '_blank';
      row.rel = 'noopener noreferrer';
      row.innerHTML = `
        <img class="sg-creator-avatar" src="${creatorAvatar}" alt="${creatorName}" />
        <div class="sg-creator-info">
          <p class="sg-creator-name">${creatorName}</p>
          <p class="sg-creator-subs">${subCount} subscribers</p>
        </div>
      `;
      dropdown.appendChild(row);
    }

    // Divider
    const div1 = document.createElement('hr');
    div1.className = 'sg-divider';
    dropdown.appendChild(div1);

    // Game info
    const gameInfo = document.createElement('div');
    gameInfo.className = 'sg-game-info';
    gameInfo.innerHTML = `
      <p class="sg-game-name">${game.name}</p>
      ${game.description ? `<p class="sg-game-desc">${game.description}</p>` : ''}
    `;
    dropdown.appendChild(gameInfo);

    // Bottom section
    const bottom = document.createElement('div');
    bottom.className = 'sg-bottom';

    // Share button
    const shareBtn = document.createElement('button');
    shareBtn.className = 'sg-bottom-btn';
    shareBtn.innerHTML = `${ICON_SHARE} Share`;
    shareBtn.addEventListener('click', () => {
      // When embedded in platform, delegate to parent (has full browser APIs)
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'subgames:share', gameId: game!.id, gameName: game!.name }, '*');
      } else {
        const shareUrl = `${config.appUrl}/game/${game!.id}`;
        if (navigator.share) {
          navigator.share({ url: shareUrl, title: game!.name }).catch(() => {});
        } else {
          navigator.clipboard.writeText(shareUrl)
            .then(() => {})
            .catch(() => window.open(shareUrl, '_blank'));
        }
      }
      isOpen = false;
      dropdown.classList.remove('sg-open');
    });
    bottom.appendChild(shareBtn);

    // Save button
    const saveBtn = document.createElement('button');
    saveBtn.className = 'sg-bottom-btn';
    saveBtn.innerHTML = `${isSaved ? ICON_BOOKMARK_FILLED : ICON_BOOKMARK} ${isSaved ? 'Saved' : 'Save'}`;
    saveBtn.addEventListener('click', async () => {
      // When embedded in platform, delegate to parent (has session cookies)
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'subgames:toggle-save', gameId: game!.id }, '*');
        const was = isSaved;
        isSaved = !was;
        saveBtn.innerHTML = `${isSaved ? ICON_BOOKMARK_FILLED : ICON_BOOKMARK} ${isSaved ? 'Saved' : 'Save'}`;
        isOpen = false;
        dropdown.classList.remove('sg-open');
        return;
      }
      const t = sdk.getToken();
      if (!t) { sdk.promptLogin(); return; }
      const was = isSaved;
      isSaved = !was;
      saveBtn.innerHTML = `${isSaved ? ICON_BOOKMARK_FILLED : ICON_BOOKMARK} ${isSaved ? 'Saved' : 'Save'}`;
      try {
        await fetch(`${config.apiUrl}/library/save/${game!.id}`, {
          method: was ? 'DELETE' : 'POST',
          headers: { Authorization: `Bearer ${t}` },
        });
      } catch {
        isSaved = was;
        saveBtn.innerHTML = `${isSaved ? ICON_BOOKMARK_FILLED : ICON_BOOKMARK} ${isSaved ? 'Saved' : 'Save'}`;
      }
    });
    bottom.appendChild(saveBtn);

    // Home link
    const homeLink = document.createElement('a');
    homeLink.className = 'sg-bottom-btn';
    homeLink.href = config.appUrl;
    homeLink.target = '_blank';
    homeLink.rel = 'noopener noreferrer';
    homeLink.innerHTML = `${ICON_HOME} Home`;
    bottom.appendChild(homeLink);

    dropdown.appendChild(bottom);
  }
}
